import React from 'react';
import { bisect } from '@skyslope/utils';

import type { FormLibrary, UserSettingsMeta } from 'store/types';
import { REQUIRED_AUTH_PROFILE_TYPES } from 'store/types';

import useGetDelegatedUserSettingsMetaQuery from './queries/useGetDelegatedUserSettingsMetaQuery';

interface UseIsAuthorizedForLibrariesState {
  authorized: FormLibrary[];
  unauthorized: FormLibrary[];
  isChecking: boolean;
  latestMeta?: UserSettingsMeta;
}

export interface LibraryAuthorizationResult extends UseIsAuthorizedForLibrariesState {
  isLoading: boolean;
}

const useIsAuthorizedForLibraries = (libraries: FormLibrary[], onBehalfOf?: string): LibraryAuthorizationResult => {
  const { isLoading, data: userSettingsMeta } = useGetDelegatedUserSettingsMetaQuery(onBehalfOf ?? '');
  const libraryProfiles = userSettingsMeta?.memberInfo.libraryProfiles;

  const authMap = React.useMemo<Record<string, boolean> | undefined>(() => {
    if (!userSettingsMeta) return undefined;
    const externalAuth = userSettingsMeta.memberInfo.externalIdAuthorization;
    const authorizedTypes = userSettingsMeta.memberInfo.authorizedTypes;

    return Object.assign(
      {},
      ...Object.keys(externalAuth)
        .flatMap((key) => externalAuth[key].map((id) => ({ [`${key}-${id}`]: true })))
        .concat(authorizedTypes.map((type) => ({ [type]: true })))
    );
  }, [userSettingsMeta]);

  return React.useMemo((): LibraryAuthorizationResult => {
    if (!authMap) return { authorized: [], unauthorized: [], isChecking: true, isLoading };
    const libraryProfileIds = libraryProfiles?.map((p) => p.libraryId);

    const [authorized, unauthorized] = bisect(
      libraries,
      (library) =>
        library.authProfiles.some(
          (a) => !REQUIRED_AUTH_PROFILE_TYPES[a.source] || authMap[`${a.source}-${a.externalId}`] || authMap[a.source]
        ) || libraryProfileIds?.includes(library.id)
    );
    return {
      authorized,
      unauthorized,
      isChecking: false,
      isLoading,
    };
  }, [authMap, isLoading, libraries, libraryProfiles]);
};

export default useIsAuthorizedForLibraries;
